import { useCreateApprovingInvoiceReportDisapprovalMutation } from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.Reject.service';
import { useApprovingInvoiceReportsSearchRefetch } from '@/context/services/approving_invoice_reports/refetchContext';
import { ApprovingInvoiceReportColumn } from '@/context/services/approving_invoice_reports/type';
import { useConfirmRejectModal } from '@/features/approving_invoice_reports/components/ConfirmRejectModal/context/ConfirmRejectModalContext';
import { ConfirmRejectForm } from '@/features/approving_invoice_reports/components/ConfirmRejectModal/types';
import { useCheckApprovingReportsProtectedResources } from '@/features/approving_invoice_reports/components/hooks/useCheckApprovingReportsProtectedResources';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTriggerFindTargetReport } from '../../List/context/TriggerFindTargetReportContext';
import { RejectSuccessAction } from '../context/ConfirmRejectModalContext';

type UseRejectReportProps = {
  onDrawerOpen: (id: string) => void;
  onDrawerClose: () => void;
};

const statusCodesCheckAccessPermission = ['401', '403', '404'];

export const useRejectReport = ({
  onDrawerOpen,
  onDrawerClose,
}: UseRejectReportProps) => {
  const notify = useToastAPI();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { rejectSuccessAction } = useConfirmRejectModal();
  const { onTriggerFindTargetReport, onTriggerBackToPageOneIfNoData } =
    useTriggerFindTargetReport();
  const { onCloseConfirmRejectModal } = useConfirmRejectModal();
  const { formState, getValues } = useFormContext<ConfirmRejectForm>();
  const refetch = useApprovingInvoiceReportsSearchRefetch();
  const { handleCheckPermission } =
    useCheckApprovingReportsProtectedResources();
  const { mutateAsync } = useCreateApprovingInvoiceReportDisapprovalMutation();

  const onSuccessFoundNextTargetReport = useCallback(
    (currentReportId: string) => {
      return (report: ApprovingInvoiceReportColumn) => {
        if (report.id === currentReportId) {
          queryClient.invalidateQueries({
            queryKey: ['approvingInvoiceReportSummary', currentReportId],
          });
        }
        onDrawerOpen(report.id);
      };
    },
    [onDrawerOpen, queryClient]
  );

  const showErrorMessages = useCallback(
    (message?: string) => {
      onTriggerBackToPageOneIfNoData();
      notify.error({
        title: message || t('system_error'),
        duration: 5,
      });
    },
    [notify, onTriggerBackToPageOneIfNoData, t]
  );

  const onErrorFoundNextTargetReport = useCallback(() => {
    onDrawerClose();
  }, [onDrawerClose]);

  const onCheckAccessApprovingReportSuccess = useCallback(
    (message?: string) => () => {
      showErrorMessages(
        message || t('approving_invoice_reports.reject_modal.error')
      );
      onDrawerClose();
    },
    [showErrorMessages, t, onDrawerClose]
  );

  const onCheckAccessApprovingReportFailure = useCallback(() => {
    onDrawerClose();
  }, [onDrawerClose]);

  const rejectReport = useCallback(
    (reportId: string) => async () => {
      if (!formState.isValid) return;
      const data = getValues();
      const resp = await mutateAsync({
        id: reportId,
        data: {
          wf_step: data.rejectDestination
            ? Number(data.rejectDestination)
            : null,
          message: data.comment,
        },
      });

      if (resp.status === 'success') {
        notify.success({
          title: t('approving_invoice_reports.reject_modal.success'),
          duration: 5,
        });
        await refetch?.();
        if (rejectSuccessAction === RejectSuccessAction.GOTO_NEXT_TARGET) {
          onTriggerFindTargetReport(
            onSuccessFoundNextTargetReport(reportId),
            onErrorFoundNextTargetReport
          );
        }
        if (rejectSuccessAction === RejectSuccessAction.REFRESH_DATA) {
          onTriggerBackToPageOneIfNoData();
        }
      } else {
        if (
          statusCodesCheckAccessPermission.includes(resp.errors?.code || '')
        ) {
          handleCheckPermission({
            fnCheckPermission: refetch,
            onCheckSuccess: onCheckAccessApprovingReportSuccess(
              resp?.errors?.messages?.join?.('')
            ),
            onCheckFailure: onCheckAccessApprovingReportFailure,
          });
        } else {
          showErrorMessages(
            resp?.errors?.messages?.join(', ') ||
              t('approving_invoice_reports.reject_modal.error')
          );
        }
      }
      onCloseConfirmRejectModal();
    },
    [
      mutateAsync,
      formState,
      getValues,
      notify,
      refetch,
      onTriggerFindTargetReport,
      onSuccessFoundNextTargetReport,
      onErrorFoundNextTargetReport,
      handleCheckPermission,
      onCheckAccessApprovingReportSuccess,
      onCheckAccessApprovingReportFailure,
      showErrorMessages,
      onCloseConfirmRejectModal,
      t,
      rejectSuccessAction,
      onTriggerBackToPageOneIfNoData,
    ]
  );
  return {
    rejectReport,
  };
};
